import type { SVGProps } from 'react'

import ChatQuestionIcon from '../../../assets/design-system/icons/ChatQuestionIcon'
import ConfirmIcon from '../../../assets/design-system/icons/ConfirmIcon'
import ErrorIcon from '../../../assets/design-system/icons/ErrorIcon'
import FileIcon from '../../../assets/design-system/icons/FileIcon'
import InfoIcon from '../../../assets/design-system/icons/InfoIcon'
import LockIcon from '../../../assets/design-system/icons/LockIcon'
import TrashIcon from '../../../assets/design-system/icons/TrashIcon'
import WarningIcon from '../../../assets/design-system/icons/WarningIcon'

import type { DialogOptions } from './types'

export type DialogIconComponent = React.FC<SVGProps<SVGSVGElement>>

export interface DialogConfig {
  icon: DialogIconComponent
  colorToken:
    | 'colorSuccess'
    | 'colorInfo'
    | 'colorError'
    | 'colorWarning'
    | 'colorTextQuaternary'
  defaults: Required<
    Omit<DialogOptions, 'onOk' | 'onCancel' | 'icon' | 'okCancel'>
  >
  danger?: boolean
}

export const DIALOG_CONFIG = {
  success: {
    icon: ConfirmIcon,
    colorToken: 'colorSuccess',
    defaults: {
      title: 'Action completed successfully',
      content: 'The action was completed without any issues.',
      okText: 'OK',
      cancelText: 'Close'
    }
  },
  information: {
    icon: InfoIcon,
    colorToken: 'colorInfo',
    defaults: {
      title: 'Heads up',
      content: 'Please review the information before proceeding.',
      okText: 'Got it',
      cancelText: 'Close'
    }
  },
  error: {
    icon: ErrorIcon,
    colorToken: 'colorError',
    defaults: {
      title: 'Unable to complete action',
      content: 'Something went wrong. Please try again or contact support.',
      okText: 'Try Again',
      cancelText: 'Close'
    }
  },
  warning: {
    icon: WarningIcon,
    colorToken: 'colorWarning',
    defaults: {
      title: 'Unsaved or incomplete data detected',
      content: 'Some data may not be saved. Do you want to go back and review?',
      okText: 'Go Back',
      cancelText: 'Continue Anyway'
    }
  },
  delete: {
    icon: TrashIcon,
    colorToken: 'colorTextQuaternary',
    danger: true,
    defaults: {
      title: 'Delete',
      content: 'This action is permanent and cannot be undone.',
      okText: 'Delete',
      cancelText: 'Cancel'
    }
  },
  unsavedChanges: {
    icon: FileIcon,
    colorToken: 'colorTextQuaternary',
    danger: true,
    defaults: {
      title: 'Unsaved changes',
      content:
        'You have unsaved changes. Leaving now will discard them. Are you sure you want to leave?',
      okText: 'Leave',
      cancelText: 'Stay on page'
    }
  },
  permissionRequired: {
    icon: LockIcon,
    colorToken: 'colorTextQuaternary',
    defaults: {
      title: 'Permission required',
      content:
        "You don't have permission to perform this action. Contact your administrator.",
      okText: 'Leave',
      cancelText: 'Close'
    }
  },
  confirm: {
    icon: ChatQuestionIcon,
    colorToken: 'colorTextQuaternary',
    defaults: {
      title: 'Confirm Action',
      content: 'Are you sure you want to proceed with this action?',
      okText: 'Leave',
      cancelText: 'Close'
    }
  }
} as const satisfies Record<string, DialogConfig>

export type DialogKind = keyof typeof DIALOG_CONFIG
